// GalleryTools (job page round): what a job's photos show, over the gallery. Show (photos and videos,
// photos, videos) and Order (newest or oldest first) are Segmented, every choice in view; Taken by opens
// a pop-over of everyone who took one, each with how many (ChoiceGroup, one of them), since a big job
// has more people than fit in a row. Show is drawn only when there are videos, and Taken by only when
// more than one person took them. While the choice hides some, a muted line says how many show, with a
// link that shows them all again. The gallery itself is the caller's: these only choose.
// Reads only --gallery-*; the controls in it read their own.
import { useState } from 'react';
import { Button } from '../Button/Button.tsx';
import { Choice, ChoiceGroup } from '../ChoiceGroup/ChoiceGroup.tsx';
import { Link } from '../Link/Link.tsx';
import { Popover } from '../Popover/Popover.tsx';
import { Segmented } from '../Segmented/Segmented.tsx';
import './GalleryTools.css';

export type GalleryKind = 'all' | 'photo' | 'video';
export type GallerySort = 'newest' | 'oldest';

/** What the gallery shows: which kind, whose ('' for everyone's), in which order. */
export interface GalleryChoice {
  kind: GalleryKind;
  /** A person's name, or '' for everyone. */
  by: string;
  sort: GallerySort;
}

export interface GalleryToolsProps {
  value: GalleryChoice;
  onChange: (value: GalleryChoice) => void;
  /** Some of them are videos: Show is drawn. */
  hasVideos: boolean;
  /** Who took them, each with how many: Taken by is drawn for two or more. */
  people: { name: string; count: number }[];
  /** How many the choice shows, of how many there are. */
  shown: number;
  total: number;
}

const EVERYONE = 'everyone';
const person = (name: string) => `by:${name}`;

export function GalleryTools({ value, onChange, hasVideos, people, shown, total }: GalleryToolsProps) {
  const [open, setOpen] = useState(false);
  const set = (part: Partial<GalleryChoice>) => onChange({ ...value, ...part });
  const hidden = shown < total;
  return (
    <div className="fl-gallery">
      <div className="fl-gallery__tools">
        {hasVideos ? (
          <Segmented
            label="Show"
            selectedKey={value.kind}
            onSelectionChange={(k) => set({ kind: k as GalleryKind })}
            items={[
              { id: 'all', label: 'Photos and videos' },
              { id: 'photo', label: 'Photos' },
              { id: 'video', label: 'Videos' },
            ]}
          />
        ) : null}
        {people.length > 1 ? (
          <Popover
            label="Taken by"
            isOpen={open}
            onOpenChange={setOpen}
            trigger={
              <Button iconEnd="chevron-down" variant={value.by ? 'primary' : 'secondary'}>
                {`Taken by: ${value.by || 'Everyone'}`}
              </Button>
            }
          >
            <ChoiceGroup
              label="Taken by"
              selectionMode="single"
              value={value.by ? person(value.by) : EVERYONE}
              onChange={(k) => {
                set({ by: k === EVERYONE ? '' : k.slice(person('').length) });
                setOpen(false);
              }}
            >
              <Choice value={EVERYONE} count={people.reduce((a, p) => a + p.count, 0)}>
                Everyone
              </Choice>
              {people.map((p) => (
                <Choice key={p.name} value={person(p.name)} count={p.count}>
                  {p.name}
                </Choice>
              ))}
            </ChoiceGroup>
          </Popover>
        ) : null}
        <Segmented
          label="Order"
          selectedKey={value.sort}
          onSelectionChange={(k) => set({ sort: k as GallerySort })}
          items={[
            { id: 'newest', label: 'Newest first' },
            { id: 'oldest', label: 'Oldest first' },
          ]}
        />
      </div>
      {hidden ? (
        <p className="fl-gallery__shown" role="status">
          {shown ? `${shown} of ${total} shown` : `None of the ${total} match`}
          <Link onPress={() => onChange({ kind: 'all', by: '', sort: value.sort })}>Show them all</Link>
        </p>
      ) : null}
    </div>
  );
}
